import { captureCoverageNote } from '../schemas/sessionCapture'
import SessionReviewers from './SessionReviewers'
import SessionSummarizers from './SessionSummarizers'
import { useMemo, useState, type ReactNode } from 'react'
import { ChevronDown, ChevronRight, ExternalLink, LoaderCircle, Route } from 'lucide-react'
import {
  useCreateProjectUserSessionJourneyMutation,
  useGetProjectUserSessionsQuery,
  type ProjectUserSession,
} from '../services/projectDataApi'
import { useAdminMode } from '../hooks/useAdminMode'
import { Link } from './nav'
import './UserSessionsTab.css'

type JourneyFilter = 'all' | 'created' | 'not-created'

function journeyActionLabel(session: ProjectUserSession): string {
  if (session.journey_status === 'inspecting_session') return 'Inspecting session…'
  if (session.journey_status === 'authoring_journey') return 'Authoring journey…'
  if (session.journey_id || session.journey_status === 'ready') return 'Retry creating journey'
  if (session.journey_status === 'failed' || session.journey_status === 'empty' || session.journey_status === 'blocked') return 'Retry creating journey'
  return 'Create reproduction journey'
}

function journeyStatus(session: ProjectUserSession): string {
  if (session.journey_id || session.journey_status === 'ready') return session.capture_coverage?.status === 'partial' ? 'Ready — partial capture' : 'Ready'
  if (session.journey_status === 'inspecting_session') return 'Inspecting with Subtext…'
  if (session.journey_status === 'authoring_journey') return 'Authoring reproduction…'
  if (session.journey_status === 'empty') return 'Empty'
  if (session.journey_status === 'failed') return 'Failed'
  if (session.journey_status === 'blocked') return 'Blocked'
  return 'Not created'
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return <div className="user-sessions__detail"><dt>{label}</dt><dd>{children}</dd></div>
}

export default function UserSessionsTab({ projectId }: { projectId: string }) {
  const adminMode = useAdminMode()
  const { data, isLoading, error } = useGetProjectUserSessionsQuery(projectId, {
    pollingInterval: 2500,
    skipPollingIfUnfocused: true,
  })
  const [createJourney] = useCreateProjectUserSessionJourneyMutation()
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const [workingSession, setWorkingSession] = useState<string | null>(null)
  const [mergeSightmap, setMergeSightmap] = useState(false)
  const [requestError, setRequestError] = useState<string | null>(null)
  const [fromDateTime, setFromDateTime] = useState('')
  const [toDateTime, setToDateTime] = useState('')
  const [journeyFilter, setJourneyFilter] = useState<JourneyFilter>('all')
  const sessions = data?.sessions ?? []
  const filteredSessions = useMemo(() => {
    const from = fromDateTime ? new Date(fromDateTime).getTime() : null
    const to = toDateTime ? new Date(toDateTime).getTime() : null

    return sessions.filter(session => {
      const receivedAt = new Date(session.last_received_at).getTime()
      if (from !== null && receivedAt < from) return false
      if (to !== null && receivedAt > to) return false
      if (journeyFilter === 'created' && !session.journey_id) return false
      if (journeyFilter === 'not-created' && session.journey_id) return false
      return true
    })
  }, [fromDateTime, journeyFilter, sessions, toDateTime])
  const hasFilters = Boolean(fromDateTime || toDateTime || journeyFilter !== 'all')

  function toggleExpanded(sessionId: string) {
    setExpanded(current => {
      const next = new Set(current)
      if (next.has(sessionId)) next.delete(sessionId)
      else next.add(sessionId)
      return next
    })
  }

  async function handleCreateJourney(sessionId: string, regenerate: boolean) {
    setWorkingSession(sessionId)
    setRequestError(null)
    try {
      await createJourney({ projectId, sessionId, regenerate, mergeSightmap }).unwrap()
      setExpanded(current => new Set(current).add(sessionId))
    } catch (cause) {
      const message = typeof cause === 'object' && cause && 'data' in cause
        ? (cause as { data?: { error?: string } }).data?.error
        : undefined
      setRequestError(message ?? 'Could not start journey creation')
    } finally {
      setWorkingSession(null)
    }
  }

  if (isLoading) return <><SessionSummarizers projectId={projectId} /><SessionReviewers projectId={projectId} /><div className="user-sessions__state">Loading user sessions…</div></>

  return (
    <>
    <SessionSummarizers projectId={projectId} />
    <SessionReviewers projectId={projectId} />
    <details><summary>Session recordings and reproduction journeys</summary>
    <section className="user-sessions" aria-labelledby="user-sessions-title">
      <header>
        <h2 id="user-sessions-title" className="project-tab-title">User Sessions</h2>
        <p className="project-tab-subtitle">Review FullStory sessions and use Subtext evidence to create portable reproduction journeys.</p>
      </header>
      <label>
        <input type="checkbox" checked={mergeSightmap} onChange={event => setMergeSightmap(event.target.checked)} />
        Update the project Sightmap while replaying newly created journeys
      </label>
      {(error || requestError) && (
        <p className="user-sessions__error" role="alert">
          {requestError ?? 'Could not load user sessions'}
        </p>
      )}
      {sessions.length > 0 && (
        <div className="user-sessions__filters" aria-label="User session filters">
          <label>
            <span>From</span>
            <input
              type="datetime-local"
              value={fromDateTime}
              max={toDateTime || undefined}
              onChange={event => setFromDateTime(event.target.value)}
              data-testid="user-sessions-from-filter"
            />
          </label>
          <label>
            <span>To</span>
            <input
              type="datetime-local"
              value={toDateTime}
              min={fromDateTime || undefined}
              onChange={event => setToDateTime(event.target.value)}
              data-testid="user-sessions-to-filter"
            />
          </label>
          <label>
            <span>Journey</span>
            <select
              value={journeyFilter}
              onChange={event => setJourneyFilter(event.target.value as JourneyFilter)}
              data-testid="user-sessions-journey-filter"
            >
              <option value="all">All sessions</option>
              <option value="created">Journey created</option>
              <option value="not-created">No journey created</option>
            </select>
          </label>
          {hasFilters && (
            <button
              type="button"
              className="user-sessions__clear-filters"
              onClick={() => {
                setFromDateTime('')
                setToDateTime('')
                setJourneyFilter('all')
              }}
            >
              Clear filters
            </button>
          )}
        </div>
      )}
      {sessions.length === 0 ? (
        <div className="user-sessions__empty">
          <p>No user sessions have been received for this project.</p>
          <p>Session ingestion and configuration are managed through the API.</p>
        </div>
      ) : filteredSessions.length === 0 ? (
        <div className="user-sessions__empty user-sessions__empty--filtered">
          <p>No user sessions match these filters.</p>
          <button
            type="button"
            onClick={() => {
              setFromDateTime('')
              setToDateTime('')
              setJourneyFilter('all')
            }}
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="user-sessions__table-wrap">
          <table>
            <thead><tr><th aria-label="Expand" /><th>Registered</th><th>User</th><th>Interactions</th></tr></thead>
            <tbody>{filteredSessions.map(session => {
              const isExpanded = expanded.has(session.session_id)
              const isWorking = workingSession === session.session_id
              const isCreating = session.journey_status === 'inspecting_session' || session.journey_status === 'authoring_journey'
              const isComplete = Boolean(session.journey_id) || session.journey_status === 'ready'
              const errorMessage = session.journey_blocked_reason === 'fullstory-session-inaccessible'
                ? 'FullStory session is not accessible.' : session.journey_error
              const networkExchangeCount = session.network_exchange_count
              return [
                <tr key={session.session_id}>
                  <td className="user-sessions__expand-cell">
                    <button
                      type="button"
                      className="user-sessions__expand"
                      aria-expanded={isExpanded}
                      aria-controls={`session-details-${session.session_id}`}
                      aria-label={`${isExpanded ? 'Collapse' : 'Expand'} session ${session.session_id}`}
                      onClick={() => toggleExpanded(session.session_id)}
                    >
                      {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                  </td>
                  <td>{new Date(session.last_received_at).toLocaleString()}</td>
                  <td>{session.user_email ?? '—'}</td>
                  <td className="user-sessions__interaction-count">{session.interaction_count ?? '—'}</td>
                </tr>,
                isExpanded && (
                  <tr key={`${session.session_id}-details`} id={`session-details-${session.session_id}`} className="user-sessions__details-row">
                    <td colSpan={4}>
                      <dl className="user-sessions__details">
                        <DetailRow label="Session ID"><code>{session.session_id}</code></DetailRow>
                        <DetailRow label="Comparison"><Link to={`/projects/${projectId}/user-sessions/${session.session_id}`}>Open session details</Link></DetailRow>
                        <DetailRow label="Source">FullStory + Subtext</DetailRow>
                        <DetailRow label="User email">{session.user_email ?? 'Not identified'}</DetailRow>
                        <DetailRow label="Registered">
                          {new Date(session.first_received_at).toLocaleString()} – {new Date(session.last_received_at).toLocaleString()}
                        </DetailRow>
                        <DetailRow label="Source session">
                          <a href={session.external_session_url} target="_blank" rel="noreferrer">
                            Open FullStory session <ExternalLink size={12} />
                          </a>
                        </DetailRow>
                        <DetailRow label="Journey status">
                          <span className={`user-sessions__status user-sessions__status--${session.journey_status ?? 'pending'}`}>
                            {journeyStatus(session)}
                          </span>
                          {session.journey_id && (
                            <Link to={`/projects/${projectId}/journeys/${session.journey_id}`}>Open journey</Link>
                          )}
                        </DetailRow>
                        {session.capture_coverage && <DetailRow label="Capture coverage">{captureCoverageNote(session.capture_coverage)}</DetailRow>}
                        <DetailRow label="Journey action">
                          <button
                            type="button"
                            className="user-sessions__journey-action"
                            disabled={isWorking || isCreating}
                            onClick={() => void handleCreateJourney(session.session_id, isComplete)}
                          >
                            {(isWorking || isCreating)
                              ? <LoaderCircle className="user-sessions__spinner" size={14} />
                              : <Route size={14} />}
                            {journeyActionLabel(session)}
                          </button>
                        </DetailRow>
                        {adminMode && session.authoring_task_id && (
                          <DetailRow label="Authoring task">
                            <Link
                              to={`/projects/${projectId}/tasks/${session.authoring_task_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-analytics-event="admin-user-session-authoring-task-link"
                            >
                              Open task
                            </Link>
                          </DetailRow>
                        )}
                        {session.journey_requested_at && (
                          <DetailRow label="Requested">{new Date(session.journey_requested_at).toLocaleString()}</DetailRow>
                        )}
                        {session.journey_completed_at && (
                          <DetailRow label="Completed">{new Date(session.journey_completed_at).toLocaleString()}</DetailRow>
                        )}
                        {session.authoring_warnings.length > 0 && (
                          <DetailRow label="Authoring warnings">{session.authoring_warnings.join(' · ')}</DetailRow>
                        )}
                        <DetailRow label="Auxiliary network data">
                          {networkExchangeCount > 0
                            ? `${networkExchangeCount} captured exchange${networkExchangeCount === 1 ? '' : 's'}`
                            : 'No embedded network capture received'}
                        </DetailRow>
                        {errorMessage && <DetailRow label={session.journey_status === 'blocked' ? 'Blocked reason' : 'Error'}><span className="user-sessions__detail-error">{errorMessage}</span></DetailRow>}
                      </dl>
                    </td>
                  </tr>
                ),
              ]
            })}</tbody>
          </table>
        </div>
      )}
    </section>
    </details>
    </>
  )
}
